import { REMINDER_MAX_DAYS, REMINDER_TIMEZONE, type SalonNote, type SalonRow } from '@manaro/contracts'
import { formatDate } from '@/shared/lib/format.ts'

// Reminder days are YYYY-MM-DD in Kyiv, like on the server: a laptop on another clock still offers
// the days the server accepts. Shifting a day works on the calendar date alone, whatever the timezone.

const pad = (value: number): string => String(value).padStart(2, '0')

const toDay = (date: Date): string => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`

const KYIV_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: REMINDER_TIMEZONE })

export const kyivToday = (now: Date = new Date()): string => KYIV_DAY.format(now)

// Noon keeps the same calendar day whatever the timezone.
const fromDay = (day: string): Date => new Date(`${day}T12:00:00`)

export const shiftDays = (day: string, days: number): string => {
  const date = fromDay(day)
  date.setDate(date.getDate() + days)
  return toDay(date)
}

/** 31 January plus a month is 28 (29) February, not 3 March. */
export const shiftMonths = (day: string, months: number): string => {
  const date = fromDay(day)
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1, 12)
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate()
  target.setDate(Math.min(date.getDate(), lastDay))
  return toDay(target)
}

export const maxReminderDay = (today: string): string => shiftDays(today, REMINDER_MAX_DAYS)

export interface ReminderPreset {
  label: string
  day: string
}

/** «Клиент попросил набрать через месяц-два» — the usual asks, one tap each. */
export const reminderPresets = (today: string): ReminderPreset[] => [
  { label: 'Завтра', day: shiftDays(today, 1) },
  { label: 'Через неделю', day: shiftDays(today, 7) },
  { label: 'Через месяц', day: shiftMonths(today, 1) },
  { label: 'Через 2 месяца', day: shiftMonths(today, 2) },
]

export const formatDay = (day: string): string => formatDate(`${day}T12:00:00`)

/** «сегодня», «завтра» or the date; an overdue one says since when. */
export function reminderWhen(day: string, today: string): string {
  if (day === today) return 'сегодня'
  if (day === shiftDays(today, 1)) return 'завтра'
  if (day < today) return `просрочено, с ${formatDay(day)}`
  return formatDay(day)
}

export const isOpenReminder = (note: SalonNote): note is SalonNote & { remindOn: string } =>
  note.remindOn !== null && note.reminderDoneAt === null

/** The nearest open reminder on a row, whoever set it — the team sees that someone will call back. */
export function nextReminder(notes: readonly SalonNote[]): (SalonNote & { remindOn: string }) | null {
  return notes.filter(isOpenReminder).reduce<(SalonNote & { remindOn: string }) | null>(
    (nearest, note) => (nearest === null || note.remindOn < nearest.remindOn ? note : nearest),
    null,
  )
}

/** `?notes=` value that opens the comments of the note's row on «Салоны». */
export const notesParam = (note: Pick<SalonNote, 'userId' | 'salonId'>): string => `${note.userId}-${note.salonId ?? 0}`

/**
 * The row a `?notes=` link points to. A note written before the owner had a salon names no salon:
 * any row of that owner shows it.
 */
export function findNotesRow(rows: readonly SalonRow[], param: string): SalonRow | null {
  const match = /^(\d+)-(\d+)$/.exec(param)
  if (!match) return null
  const userId = Number(match[1])
  const salonId = Number(match[2])
  const ownerRows = rows.filter((row) => row.user.id === userId)
  return ownerRows.find((row) => (row.salon?.id ?? 0) === salonId) ?? (salonId === 0 ? (ownerRows[0] ?? null) : null)
}
